// This file is auto-generated by @hey-api/openapi-ts

import type { CancelablePromise } from "./core/CancelablePromise";
import { OpenAPI } from "./core/OpenAPI";
import { request as __request } from "./core/request";
import type {
  RestRestCheckRetrieveResponse,
  UsersListData,
  UsersListResponse,
  UsersCreateData,
  UsersCreateResponse,
  UsersRetrieveData,
  UsersRetrieveResponse,
  UsersUpdateData,
  UsersUpdateResponse,
  UsersPartialUpdateData,
  UsersPartialUpdateResponse,
  UsersDestroyData,
  UsersDestroyResponse,
} from "./types.gen";
import type { Rest, User, UserFact } from './schemas.simple';

export class RestService {
  /**
   * Check REST API
   * This endpoint checks if the REST API is working.
   * @returns Rest
   * @throws ApiError
   */
  public static async restRestCheckRetrieve(): Promise<Rest> {
    const result: unknown = await __request(OpenAPI, {
      method: 'GET',
      url: '/api/rest/rest-check/',
    });
    return (result as any).body;
  }
}

export class UsersService {
  /**
   * @param data The data for the request.
   * @param data.limit Number of results to return per page.
   * @param data.offset The initial index from which to return the results.
   * @returns PaginatedUserList
   * @throws ApiError
   */
  public static usersList(
    data: UsersListData = {},
  ): CancelablePromise<UsersListResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/users/",
      query: {
        limit: data.limit,
        offset: data.offset,
      },
    });
  }

  /**
   * @param data The data for the request.
   * @param data.requestBody
   * @returns User
   * @throws ApiError
   */
  public static usersCreate(
    data: UsersCreateData,
  ): CancelablePromise<UsersCreateResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/users/",
      body: data.requestBody,
      mediaType: "application/json",
    });
  }

  /**
   * @returns User
   * @throws ApiError
   */
  public static async usersRetrieve(): Promise<User> {
    const result = await __request(OpenAPI, {
      method: 'GET',
      url: '/api/users/',
    });
    const typedResult = result as { body: User };
    return typedResult.body;
  }

  /**
   * @param data The data for the request.
   * @param data.id A unique integer value identifying this user.
   * @param data.requestBody
   * @returns User
   * @throws ApiError
   */
  public static usersUpdate(
    data: UsersUpdateData,
  ): CancelablePromise<UsersUpdateResponse> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/api/users/{id}/",
      path: {
        id: data.id,
      },
      body: data.requestBody,
      mediaType: "application/json",
    });
  }

  /**
   * @param data The data for the request.
   * @param data.id A unique integer value identifying this user.
   * @param data.requestBody
   * @returns User
   * @throws ApiError
   */
  public static usersPartialUpdate(
    data: UsersPartialUpdateData,
  ): CancelablePromise<UsersPartialUpdateResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/api/users/{id}/",
      path: {
        id: data.id,
      },
      body: data.requestBody,
      mediaType: "application/json",
    });
  }

  /**
   * @param data The data for the request.
   * @param data.id A unique integer value identifying this user.
   * @returns void No response body
   * @throws ApiError
   */
  public static usersDestroy(
    data: UsersDestroyData,
  ): CancelablePromise<UsersDestroyResponse> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/api/users/{id}/",
      path: {
        id: data.id,
      },
    });
  }
}

export class UserFactService {
  /**
   * Get all user facts for the current user
   * @param url Optional URL for pagination
   * @param showDeleted Optional flag to show only deleted memories
   * @returns Object containing results array and pagination info
   * @throws ApiError
   */
  public static async userFactsList(url?: string, showDeleted?: boolean): Promise<{
    results: UserFact[];
    count: number;
    next: string | null;
    previous: string | null;
  }> {
    try {
      let requestUrl = url || '/api/memories/';

      // Add show_deleted parameter if specified
      if (showDeleted) {
        const separator = requestUrl.includes('?') ? '&' : '?';
        requestUrl += `${separator}show_deleted=true`;
      }

      const result: unknown = await __request(OpenAPI, {
        method: 'GET',
        url: requestUrl,
        errors: {
          401: `Unauthorized`,
        },
      });

      // The response is an object with results, count, next, previous
      if (result && typeof result === 'object' && 'results' in result) {
        const typedResult = result as {
          results: UserFact[];
          count: number;
          next: string | null;
          previous: string | null;
        };
        return typedResult;
      }

      // For backward compatibility - if the response is an array of UserFacts
      if (Array.isArray(result)) {
        return {
          results: result as UserFact[],
          count: result.length,
          next: null,
          previous: null
        };
      }

      // Return empty object as fallback
      return { results: [], count: 0, next: null, previous: null };
    } catch (error) {
      console.error("Error in userFactsList:", error);
      throw error;
    }
  }

  /**
   * Get a specific user fact
   * @param id
   * @returns UserFact
   * @throws ApiError
   */
  public static async userFactsRetrieve(
    id: string,
  ): Promise<UserFact> {
    const result: unknown = await __request(OpenAPI, {
      method: 'GET',
      url: '/api/memories/{id}/',
      path: {
        id: id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not found`,
      },
    });
    return (result as any).body;
  }

  /**
   * Create a new user fact
   * @param requestBody
   * @returns UserFact
   * @throws ApiError
   */
  public static async userFactsCreate(
    requestBody: UserFact,
  ): Promise<UserFact> {
    const result: unknown = await __request(OpenAPI, {
      method: 'POST',
      url: '/api/memories/',
      body: requestBody,
      errors: {
        401: `Unauthorized`,
        400: `Bad request`,
      },
    });
    return (result as any).body;
  }

  /**
   * Update a user fact
   * @param id
   * @param requestBody
   * @returns UserFact
   * @throws ApiError
   */
  public static async userFactsUpdate(
    id: string,
    requestBody: UserFact,
  ): Promise<UserFact> {
    const result: unknown = await __request(OpenAPI, {
      method: 'PUT',
      url: '/api/memories/{id}/',
      path: {
        id: id,
      },
      body: requestBody,
      errors: {
        401: `Unauthorized`,
        404: `Not found`,
        400: `Bad request`,
      },
    });
    return (result as any).body;
  }

  /**
   * Partial update a user fact
   * @param id
   * @param requestBody
   * @returns UserFact
   * @throws ApiError
   */
  public static async userFactsPartialUpdate(
    id: string,
    requestBody: Partial<UserFact>,
  ): Promise<UserFact> {
    const result: unknown = await __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/memories/{id}/',
      path: {
        id: id,
      },
      body: requestBody,
      errors: {
        401: `Unauthorized`,
        404: `Not found`,
        400: `Bad request`,
      },
    });
    return (result as any).body;
  }

  /**
   * Delete a user fact
   * @param id
   * @returns void
   * @throws ApiError
   */
  public static async userFactsDelete(
    id: string,
  ): Promise<void> {
    await __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/memories/{id}/',
      path: {
        id: id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not found`,
      },
    });
  }

  /**
   * Get available memory types
   * @returns Record<string, string>
   * @throws ApiError
   */
  public static async userFactsCategories(): Promise<Record<string, string>> {
    const result: unknown = await __request(OpenAPI, {
      method: 'GET',
      url: '/api/memories/categories/',
      errors: {
        401: `Unauthorized`,
      },
    });
    return result as Record<string, string>;
  }
}
